// Pupil Detail Page - SAT V4 Zone
import { DsButton, DsCard, DsIcon, DsTypography } from '@ice-web-app/designSystem';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import type { FC, SyntheticEvent } from 'react';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUserManagementStore } from '../../../../stores';
import { routes } from '../../../../routes/routes';
import { pupilDetailTabConfigs } from '../../MockData';
import ProfileTab from './ProfileTab';
import SubjectsTab from './SubjectsTab';
import SpecialRequirementsTab from './SpecialRequirementsTab';
import ParentsCarersTab from './ParentsCarersTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`pupil-detail-tabpanel-${index}`}
    aria-labelledby={`pupil-detail-tab-${index}`}
    sx={{ pt: 3 }}
  >
    {value === index && children}
  </Box>
);

const PupilDetail: FC = () => {
  const { pupilId } = useParams<{ pupilId: string }>();
  const navigate = useNavigate();
  const { getPupilById, updatePupil } = useUserManagementStore();
  const [activeTab, setActiveTab] = useState(0);

  const pupil = pupilId ? getPupilById(pupilId) : undefined;

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(routes.userManagement);
  };

  const handleUpdate = (updates: Partial<typeof pupil>) => {
    if (pupilId) {
      updatePupil(pupilId, updates);
    }
  };

  if (!pupil) {
    return (
      <Box sx={{ p: 3 }}>
        <DsCard sx={{ p: 4, textAlign: 'center' }}>
          <DsIcon icon="ALERT_WARNING" sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
          <DsTypography variant="h6" gutterBottom>
            Pupil Not Found
          </DsTypography>
          <DsTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The pupil with ID "{pupilId}" could not be found.
          </DsTypography>
          <DsButton variant="contained" onClick={handleBack}>
            Back to Pupils
          </DsButton>
        </DsCard>
      </Box>
    );
  }

  const tabComponents = [
    <ProfileTab key="profile" pupil={pupil} onUpdate={handleUpdate} />,
    <SubjectsTab key="subjects" pupil={pupil} onUpdate={handleUpdate} />,
    <SpecialRequirementsTab key="specialRequirements" pupil={pupil} onUpdate={handleUpdate} />,
    <ParentsCarersTab key="parentsCarers" pupil={pupil} onUpdate={handleUpdate} />,
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <DsButton
            variant="text"
            startIcon={<DsIcon icon="ARROW_LEFT" />}
            onClick={handleBack}
          >
            Back
          </DsButton>
          <Box>
            <DsTypography variant="h4" fontWeight={700}>
              {pupil.firstName} {pupil.lastName}
            </DsTypography>
            <DsTypography variant="body2" color="text.secondary">
              {pupil.studentId} • Year {pupil.yearGroup} • {pupil.classId}
            </DsTypography>
          </Box>
        </Stack>

        {/* Tabs Card */}
        <DsCard sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="Pupil detail tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              {pupilDetailTabConfigs.map((config, index) => (
                <Tab
                  key={config.id}
                  label={config.label}
                  id={`pupil-detail-tab-${index}`}
                  aria-controls={`pupil-detail-tabpanel-${index}`}
                  disabled={index === 2 || index === 3}
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>
            {tabComponents.map((component, index) => (
              <TabPanel key={index} value={activeTab} index={index}>
                {component}
              </TabPanel>
            ))}
          </Box>
        </DsCard>
      </Stack>
    </Box>
  );
};

export default PupilDetail;

